export { WebCodecsWorkerRuntime } from './WebCodecsWorkerRuntime';
export type { WcWorkerInput, WcWorkerOutput, WcWorkerPlatform } from '../client/WebCodecsClientTypes';
import type { WcWorkerPlatform, WcDecoderBackend, WcRenderSurface, WcVideoFrame } from '../client/WebCodecsClientTypes';
import { contextLost, flushDraw, resizeKeepingPicture } from './WebCodecsSurface';

/** Browser adapter; importing this module never starts a worker or touches a DOM surface. */
export class WebCodecsBrowserPlatform implements WcWorkerPlatform {
  constructor(private readonly clock: () => number, private readonly animation: {
    requestAnimationFrame(callback: FrameRequestCallback): number; cancelAnimationFrame(id: number): void;
  }) {}
  nowUs(): number { return this.clock(); }
  setTimer(callback: () => void, milliseconds: number): number { return globalThis.setTimeout(callback, milliseconds) as unknown as number; }
  clearTimer(id: number): void { globalThis.clearTimeout(id); }
  requestFrame(callback: () => void): number { return this.animation.requestAnimationFrame(callback); }
  cancelFrame(id: number): void { this.animation.cancelAnimationFrame(id); }
  isConfigSupported(config: VideoDecoderConfig): Promise<VideoDecoderSupport> { return VideoDecoder.isConfigSupported(config); }
  createDecoder(output: (frame: WcVideoFrame) => void, error: (error: Error) => void): WcDecoderBackend {
    const decoder = new VideoDecoder({ output, error });
    return { get decodeQueueSize() { return decoder.decodeQueueSize; }, configure: config => decoder.configure(config),
      decode: chunk => decoder.decode(new EncodedVideoChunk(chunk)), close: () => { if (decoder.state !== 'closed') decoder.close(); } };
  }
  createSurface(canvas: OffscreenCanvas): WcRenderSurface {
    // Not desynchronized: with that hint the compositor may publish the canvas before a draw has
    // landed, and the VideoFrame is closed right after drawImage, so the viewer saw a black frame.
    const context = canvas.getContext('2d', { alpha: false });
    if (!context) throw new Error('OffscreenCanvas 2D rendering unavailable');
    return {
      resize: (width, height) => {
        resizeKeepingPicture(canvas, width, height, image => context.drawImage(image as CanvasImageSource, 0, 0, width, height));
      },
      draw: frame => {
        if (contextLost(context)) throw new Error('Canvas context lost');
        context.drawImage(frame as VideoFrame, 0, 0, canvas.width, canvas.height);
        // Finish the draw before the frame is closed; otherwise the pixels may never be read.
        flushDraw(context);
      },
      onLost: listener => { canvas.addEventListener('contextlost', listener); return () => canvas.removeEventListener('contextlost', listener); },
    };
  }
}
